import { useEffect, useState } from "react";
import { ArrowDown, ArrowDownUp, ArrowUp, FileSpreadsheet, ImagePlus, Pencil, Plus, Search, Warehouse } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, fmtTL, API, QUALITY_OPTIONS } from "../lib/api";
import { fmtQty4 } from "../lib/format";
import QualityBadge from "../components/QualityBadge";
import MediaPicker from "../components/MediaPicker";
import SmartCategoryPicker from "../components/SmartCategoryPicker";
import ConfirmDialog from "../components/ConfirmDialog";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";

const UNITS = ["kg", "çuval", "teneke", "kova", "adet", "litre", "100 gr"];
const fmtKB = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function Inventory() {
    const [data, setData] = useState(null);
    const [adjust, setAdjust] = useState(null);
    const [newStock, setNewStock] = useState("");
    const [categories, setCategories] = useState([]);
    const [addOpen, setAddOpen] = useState(false);
    const [barcodeMode, setBarcodeMode] = useState("barkodsuz");
    const emptyAdd = { name: "", category_id: "", unit: "kg", price: "", stock: "", moq: "5", delivery_days: "3", description: "", quality: "" };
    const [addForm, setAddForm] = useState(emptyAdd);
    const [img, setImg] = useState(null);
    const [saving, setSaving] = useState(false);
    const [edit, setEdit] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [editImg, setEditImg] = useState(null);
    const [pickerFor, setPickerFor] = useState(null);
    const [sug, setSug] = useState([]);

    useEffect(() => {
        const name = (addForm.name || "").trim();
        if (!addOpen || name.length < 3) { setSug([]); return; }
        const t = setTimeout(async () => {
            let items = [];
            try {
                const r = await api.get(`/media?limit=6&q=${encodeURIComponent(name)}`);
                items = r.data.items;
                if (!items.length && name.includes(" ")) {
                    const r2 = await api.get(`/media?limit=6&q=${encodeURIComponent(name.split(" ")[0])}`);
                    items = r2.data.items;
                }
            } catch { /* öneri yok */ }
            setSug(items);
        }, 400);
        return () => clearTimeout(t);
    }, [addForm.name, addOpen]);
    const [bulkMode, setBulkMode] = useState(false);
    const [bulkEdits, setBulkEdits] = useState({});
    const [confirmZero, setConfirmZero] = useState(false);
    const [excelOpen, setExcelOpen] = useState(false);
    const [excelPreview, setExcelPreview] = useState(null);
    const [excelBusy, setExcelBusy] = useState(false);
    const [excelHistory, setExcelHistory] = useState(null);
    const [excelModule, setExcelModule] = useState("stock");
    const [q, setQ] = useState("");
    const [catFilter, setCatFilter] = useState("all");
    const [stockFilter, setStockFilter] = useState("all");
    const [sort, setSort] = useState({ key: "name", dir: 1 });

    const downloadFile = (url) => {
        const a = document.createElement("a");
        a.href = url;
        a.click();
    };

    const openHistory = async () => {
        try {
            const r = await api.get("/excel/history");
            setExcelHistory(r.data);
        } catch (e) {
            toast.error("Geçmiş alınamadı");
        }
    };

    const uploadExcel = async (file) => {
        if (!file) return;
        setExcelBusy(true);
        setExcelPreview(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post(`/excel/import?module=${excelModule}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setExcelPreview(r.data);
            if (r.data.failed > 0) toast.warning(`${r.data.failed} satırda hata var — yalnızca geçerli satırlar uygulanacak`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Excel okunamadı");
        } finally {
            setExcelBusy(false);
        }
    };

    const commitExcel = async () => {
        setExcelBusy(true);
        try {
            const r = await api.post(`/excel/import/${excelPreview.import_id}/commit`);
            toast.success(`${r.data.applied} kayıt uygulandı (${excelPreview.import_id})`);
            setExcelPreview(null);
            setExcelOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Uygulanamadı");
        } finally {
            setExcelBusy(false);
        }
    };

    const setBulkField = (offerId, field, value, item) => {
        setBulkEdits((prev) => ({
            ...prev,
            [offerId]: { price: prev[offerId]?.price ?? item.price, stock: prev[offerId]?.stock ?? item.stock, [field]: value },
        }));
    };

    const saveBulk = async () => {
        const updates = Object.entries(bulkEdits).map(([offer_id, v]) => ({
            offer_id,
            price: v.price !== undefined ? Number(v.price) : undefined,
            stock: v.stock !== undefined ? Number(v.stock) : undefined,
        }));
        try {
            const r = await api.post("/inventory/bulk-update", { updates });
            toast.success(`${r.data.applied} üründe fiyat/stok güncellendi`);
            setBulkMode(false);
            setBulkEdits({});
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Güncellenemedi");
        }
    };

    const load = () => api.get("/inventory").then((r) => setData(r.data));
    useEffect(() => {
        load().catch(() => {});
        api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    }, []);

    const uploadImage = async (file, setter) => {
        if (!file) return;
        const preview = URL.createObjectURL(file);
        setter({ preview, url: null, stats: null, uploading: true });
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/supplier/products/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setter({ preview, url: r.data.url, stats: { orig: r.data.original_size, comp: r.data.compressed_size }, uploading: false });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Görsel yüklenemedi");
            setter(null);
        }
    };

    const submitAdd = async () => {
        if (barcodeMode === "barkodlu" && !(addForm.barcode || "").trim()) return toast.error("Barkodlu seçildi — barkod numarası zorunludur");
        if (!addForm.name.trim() || !addForm.category_id || !addForm.price || addForm.stock === "") {
            return toast.error("Ürün adı, kategori, fiyat ve stok zorunludur");
        }
        setSaving(true);
        try {
            await api.post("/supplier/products", {
                ...addForm,
                price: Number(addForm.price),
                stock: Number(addForm.stock),
                moq: Number(addForm.moq) || 1,
                delivery_days: Number(addForm.delivery_days) || 3,
                image: img?.url,
            });
            toast.success("Ürün kataloğa ve envanterinize eklendi");
            setAddOpen(false);
            setAddForm(emptyAdd);
            setImg(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Ürün eklenemedi");
        } finally {
            setSaving(false);
        }
    };

    const openEdit = (item) => {
        setEdit(item);
        setEditForm({ price: item.price, stock: item.stock, moq: item.moq, delivery_days: item.delivery_days, quality: item.quality || "" });
        setEditImg(null);
    };

    const submitEdit = async () => {
        setSaving(true);
        try {
            await api.patch(`/supplier/products/${edit.offer_id}`, {
                price: Number(editForm.price),
                stock: Number(editForm.stock),
                moq: Number(editForm.moq),
                delivery_days: Number(editForm.delivery_days),
                quality: editForm.quality || "",
                image: editImg?.url,
            });
            toast.success("Ürün güncellendi");
            setEdit(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Güncellenemedi");
        } finally {
            setSaving(false);
        }
    };

    const doSave = async () => {
        try {
            await api.post("/inventory/adjust", { offer_id: adjust.offer_id, stock: Number(newStock) });
            toast.success("Stok güncellendi");
            setAdjust(null);
            setConfirmZero(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Güncellenemedi");
            setConfirmZero(false);
        }
    };

    const save = () => {
        if (Number(newStock) === 0 && Number(newStock) !== Number(adjust.stock)) {
            setConfirmZero(true);
            return;
        }
        doSave();
    };

    if (!data) return <div className="h-96 animate-pulse rounded-2xl bg-muted" />;

    const lowCount = data.items.filter((i) => i.low_stock).length;
    const catsInUse = [...new Set(data.items.map((i) => i.category_id).filter(Boolean))];
    const items = data.items
        .filter((i) => !q || i.product_name.toLowerCase().includes(q.toLowerCase()))
        .filter((i) => catFilter === "all" || i.category_id === catFilter)
        .filter((i) => stockFilter === "all" || (stockFilter === "low" ? i.low_stock : !i.low_stock))
        .sort((a, b) => {
            const k = sort.key;
            const va = k === "name" ? a.product_name : a[k];
            const vb = k === "name" ? b.product_name : b[k];
            return sort.dir * (typeof va === "string" ? String(va).localeCompare(String(vb), "tr") : (va ?? 0) - (vb ?? 0));
        });

    const SortTh = ({ k, children, className = "p-4" }) => (
        <th className={className}>
            <button onClick={() => setSort((s) => ({ key: k, dir: s.key === k ? -s.dir : 1 }))} className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground" data-testid={`inventory-sort-${k}`}>
                {children} {sort.key === k && (sort.dir === 1 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
            </button>
        </th>
    );

    return (
        <div className="space-y-6" data-testid="inventory-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Stok & Envanter</h1>
                    <p className="mt-1 text-sm text-muted-foreground">Ürün bazlı satılabilir stok durumunuz</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {lowCount > 0 && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800" data-testid="low-stock-warning">
                            <Warehouse className="mr-1.5 inline h-4 w-4" /> {lowCount} üründe kritik stok
                        </div>
                    )}
                    <Button variant="outline" size="sm" onClick={() => downloadFile(`${API}/excel/template?module=stock`)} data-testid="excel-template-button">
                        <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Şablon
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setExcelOpen(true); setExcelPreview(null); setExcelHistory(null); }} data-testid="excel-import-button">
                        <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel'den Yükle
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => downloadFile(`${API}/excel/export?module=stock`)} data-testid="excel-export-button">
                        <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel'e Aktar
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => downloadFile(`${API}/excel/export?module=products`)} data-testid="excel-export-products-button">
                        <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Ürünleri Aktar
                    </Button>
                    <Button
                        variant={bulkMode ? "default" : "outline"}
                        className={bulkMode ? "bg-sage-600 text-white hover:bg-sage-600/90" : ""}
                        onClick={() => { setBulkMode(!bulkMode); setBulkEdits({}); }}
                        data-testid="bulk-edit-toggle"
                    >
                        <Pencil className="mr-2 h-4 w-4" /> {bulkMode ? "Düzenlemeyi Bitir" : "Toplu Düzenle"}
                    </Button>
                    <Button onClick={() => setAddOpen(true)} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="add-product-button">
                        <Plus className="mr-2 h-4 w-4" /> Ürün Ekle
                    </Button>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2" data-testid="inventory-filters">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün ara..." className="h-9 w-56 pl-8 text-sm" data-testid="inventory-search" />
                </div>
                <div className="w-64">
                    <SmartCategoryPicker categories={categories} value={catFilter} onChange={setCatFilter} allowAll allLabel="Tüm Kategoriler" tid="inventory-category-filter" className="h-9 text-xs" />
                </div>
                <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} className="h-9 rounded-md border bg-background px-2 text-xs" data-testid="inventory-stock-filter">
                    <option value="all">Stok: Tümü</option>
                    <option value="low">Kritik Stok</option>
                    <option value="normal">Normal</option>
                </select>
                <span className="ml-auto text-xs text-muted-foreground">{items.length} ürün</span>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                <div className="overflow-hidden rounded-2xl border bg-card shadow-sm lg:col-span-8">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="inventory-table">
                            <thead>
                                <tr className="border-b bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                                    <SortTh k="name">Ürün</SortTh>
                                    <SortTh k="price">Liste Fiyatı (KDV dahil)</SortTh>
                                    <th className="p-4">MOQ</th>
                                    <SortTh k="stock">Stok</SortTh>
                                    <th className="p-4">Durum</th>
                                    <th className="p-4 text-right">İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it) => (
                                    <tr key={it.offer_id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`inventory-row-${it.offer_id}`}>
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                <img src={it.image} alt="" className="h-10 w-10 rounded-lg object-cover" />
                                                <span className="font-semibold">{it.product_name}</span>
                                                <QualityBadge quality={it.quality} tid={it.offer_id} />
                                            </div>
                                        </td>
                                        <td className="p-4 font-medium">
                                            {bulkMode ? (
                                                <Input type="number" min="0" className="h-8 w-24" value={bulkEdits[it.offer_id]?.price ?? it.price} onChange={(e) => setBulkField(it.offer_id, "price", e.target.value, it)} data-testid={`bulk-price-${it.offer_id}`} />
                                            ) : (
                                                <>{fmtTL(it.price)} / {it.unit}</>
                                            )}
                                        </td>
                                        <td className="p-4 text-muted-foreground">{fmtQty4(it.moq, it.unit)} {it.unit}</td>
                                        <td className="p-4 font-display font-extrabold" data-testid={`stock-value-${it.offer_id}`}>
                                            {bulkMode ? (
                                                <Input type="number" min="0" className="h-8 w-20" value={bulkEdits[it.offer_id]?.stock ?? it.stock} onChange={(e) => setBulkField(it.offer_id, "stock", e.target.value, it)} data-testid={`bulk-stock-${it.offer_id}`} />
                                            ) : (
                                                <>{fmtQty4(it.stock, it.unit)} {it.unit}</>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            {it.low_stock ? (
                                                <Badge variant="outline" className="border-amber-200 bg-amber-100 text-[10px] text-amber-800" data-testid={`low-stock-badge-${it.offer_id}`}>Kritik</Badge>
                                            ) : (
                                                <Badge variant="outline" className="border-emerald-200 bg-emerald-100 text-[10px] text-emerald-800">Normal</Badge>
                                            )}
                                        </td>
                                        <td className="p-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button size="sm" variant="outline" onClick={() => openEdit(it)} data-testid={`edit-product-${it.offer_id}`}>
                                                    <Pencil className="mr-1.5 h-3.5 w-3.5" /> Düzenle
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => { setAdjust(it); setNewStock(String(it.stock)); }} data-testid={`adjust-stock-${it.offer_id}`}>
                                                    <ArrowDownUp className="mr-1.5 h-3.5 w-3.5" /> Düzelt
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="rounded-2xl border bg-card p-5 shadow-sm lg:col-span-4" data-testid="stock-movements">
                    <h3 className="font-display font-bold">Son Stok Hareketleri</h3>
                    <div className="mt-4 space-y-3">
                        {data.movements.slice(0, 12).map((m) => (
                            <div key={m.movement_id} className="flex items-center justify-between border-b pb-2.5 text-sm last:border-0">
                                <div>
                                    <p className="font-medium">{m.product_name}</p>
                                    <p className="text-xs text-muted-foreground">{m.ref} • {fmtDate(m.at)}</p>
                                </div>
                                <span className={`font-display font-extrabold ${m.type === "out" || m.qty < 0 ? "text-red-600" : "text-emerald-700"}`}>
                                    {m.qty > 0 ? "+" : ""}{fmtQty4(m.qty, m.unit)} {m.unit}
                                </span>
                            </div>
                        ))}
                        {!data.movements.length && <p className="text-sm text-muted-foreground">Hareket yok.</p>}
                    </div>
                </div>
            </div>

            {bulkMode && (
                <div className="fixed bottom-4 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-full border border-stone-700 bg-stone-950 px-5 py-3 text-white shadow-2xl" data-testid="bulk-save-bar">
                    <span className="text-xs" data-testid="bulk-changes-count">{Object.keys(bulkEdits).length} üründe değişiklik</span>
                    <Button size="sm" variant="ghost" className="text-stone-300 hover:text-white" onClick={() => { setBulkMode(false); setBulkEdits({}); }}>Vazgeç</Button>
                    <Button size="sm" className="bg-terra-600 text-white hover:bg-terra-700" onClick={saveBulk} data-testid="bulk-save-button">Kaydet</Button>
                </div>
            )}

            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent className="max-w-lg" data-testid="add-product-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Yeni Ürün Ekle</DialogTitle>
                        <DialogDescription>Ürün platform kataloğuna eklenir ve teklifiniz listelenir.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Input placeholder="Ürün adı * (örn: Antep Fıstığı İçi)" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} data-testid="add-product-name" />
                        <div className="grid grid-cols-2 gap-3">
                            <SmartCategoryPicker categories={categories} value={addForm.category_id} onChange={(v) => setAddForm({ ...addForm, category_id: v })} placeholder="Kategori *" tid="add-product-category" />
                            <Select value={addForm.unit} onValueChange={(v) => setAddForm({ ...addForm, unit: v })}>
                                <SelectTrigger data-testid="add-product-unit"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Select value={addForm.quality || "none"} onValueChange={(v) => setAddForm({ ...addForm, quality: v === "none" ? "" : v })}>
                                <SelectTrigger data-testid="add-product-quality"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Kalite: Belirtilmedi</SelectItem>
                                    {QUALITY_OPTIONS.map((qo) => <SelectItem key={qo} value={qo}>{qo}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Input type="number" min="0" placeholder="Fiyat (₺) *" value={addForm.price} onChange={(e) => setAddForm({ ...addForm, price: e.target.value })} data-testid="add-product-price" />
                            <Input type="number" min="0" placeholder="Stok *" value={addForm.stock} onChange={(e) => setAddForm({ ...addForm, stock: e.target.value })} data-testid="add-product-stock" />
                            <Input type="number" min="1" placeholder="Min. sipariş (MOQ)" value={addForm.moq} onChange={(e) => setAddForm({ ...addForm, moq: e.target.value })} data-testid="add-product-moq" />
                            <Input type="number" min="1" placeholder="Teslim (gün)" value={addForm.delivery_days} onChange={(e) => setAddForm({ ...addForm, delivery_days: e.target.value })} data-testid="add-product-delivery" />
                        </div>
                        <Textarea placeholder="Açıklama (opsiyonel)" rows={2} value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })} data-testid="add-product-description" />
                        <div>
                            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Barkod</p>
                            <div className="flex gap-2" data-testid="add-product-barcode-mode">
                                {[["barkodlu", "Barkodlu"], ["barkodsuz", "Barkodsuz"]].map(([k, l]) => (
                                    <button key={k} type="button" onClick={() => setBarcodeMode(k)} className={`flex-1 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${barcodeMode === k ? "border-terra-600 bg-terra-600 text-white" : "text-muted-foreground hover:border-terra-400"}`} data-testid={`barcode-mode-${k}`}>{l}</button>
                                ))}
                            </div>
                            {barcodeMode === "barkodlu" && (
                                <Input className="mt-2" placeholder="Barkod numarası (EAN-13) *" value={addForm.barcode || ""} onChange={(e) => setAddForm({ ...addForm, barcode: e.target.value })} data-testid="add-product-barcode" />
                            )}
                        </div>
                        <div>
                            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ürün Görseli</p>
                            <label className="flex cursor-pointer items-center gap-4 rounded-xl border border-dashed p-4 transition-colors hover:border-terra-500/50" data-testid="add-product-image-drop">
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files[0], setImg)} data-testid="add-product-image-input" />
                                {img?.preview ? (
                                    <img src={img.preview} alt="" className="h-16 w-16 rounded-lg object-cover" />
                                ) : (
                                    <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-muted"><ImagePlus className="h-6 w-6 text-muted-foreground" /></div>
                                )}
                                <div className="text-xs text-muted-foreground">
                                    <p className="font-semibold text-foreground">
                                        {img ? (img.uploading ? "Yükleniyor ve sıkıştırılıyor..." : "Görsel hazır") : "Görsel seç (JPG, PNG — maks. 20 MB)"}
                                    </p>
                                    {img?.stats && (
                                        <p className="mt-0.5 font-medium text-emerald-700" data-testid="add-product-compression-info">
                                            {fmtKB(img.stats.orig)} → {fmtKB(img.stats.comp)} • WebP, maks. 1600px
                                        </p>
                                    )}
                                    {!img && <p className="mt-0.5">Otomatik WebP sıkıştırma uygulanır — kalite korunur</p>}
                                </div>
                            </label>
                            <button type="button" onClick={() => setPickerFor("add")} className="mt-2 text-xs font-medium text-primary hover:underline" data-testid="add-product-pick-library">
                                veya Ortam Kütüphanesi'nden seç
                            </button>
                            {sug.length > 0 && !img && (
                                <div className="mt-2" data-testid="add-product-suggestions">
                                    <p className="mb-1 text-[11px] font-medium text-muted-foreground">Önerilen görseller (ürün adına göre):</p>
                                    <div className="flex flex-wrap gap-2">
                                        {sug.map((m) => (
                                            <button key={m.id} type="button" onClick={() => setImg({ preview: m.url, url: m.url, stats: null, uploading: false })} className="h-14 w-14 overflow-hidden rounded-lg border transition-shadow hover:ring-2 hover:ring-terra-500" data-testid={`add-sug-${m.id}`} title={m.original_filename}>
                                                <img src={m.url} alt="" className="h-full w-full object-cover" loading="lazy" />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddOpen(false)}>Vazgeç</Button>
                        <Button onClick={submitAdd} disabled={saving || img?.uploading} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="add-product-submit">
                            {saving ? "Ekleniyor..." : "Ürünü Ekle"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
                <DialogContent data-testid="edit-product-dialog">
                    {edit && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display">Teklifi Düzenle — {edit.product_name}</DialogTitle>
                                <DialogDescription>Fiyat, stok, MOQ ve teslim süresini güncelleyin; görseli değiştirebilirsiniz.</DialogDescription>
                            </DialogHeader>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <p className="mb-1 text-xs font-medium text-muted-foreground">Fiyat (₺)</p>
                                    <Input type="number" min="0" value={editForm.price ?? ""} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} data-testid="edit-product-price" />
                                </div>
                                <div>
                                    <p className="mb-1 text-xs font-medium text-muted-foreground">Stok</p>
                                    <Input type="number" min="0" value={editForm.stock ?? ""} onChange={(e) => setEditForm({ ...editForm, stock: e.target.value })} data-testid="edit-product-stock" />
                                </div>
                                <div>
                                    <p className="mb-1 text-xs font-medium text-muted-foreground">MOQ</p>
                                    <Input type="number" min="1" value={editForm.moq ?? ""} onChange={(e) => setEditForm({ ...editForm, moq: e.target.value })} data-testid="edit-product-moq" />
                                </div>
                                <div>
                                    <p className="mb-1 text-xs font-medium text-muted-foreground">Teslim (gün)</p>
                                    <Input type="number" min="1" value={editForm.delivery_days ?? ""} onChange={(e) => setEditForm({ ...editForm, delivery_days: e.target.value })} data-testid="edit-product-delivery" />
                                </div>
                                <div>
                                    <p className="mb-1 text-xs font-medium text-muted-foreground">Kalite</p>
                                    <Select value={editForm.quality || "none"} onValueChange={(v) => setEditForm({ ...editForm, quality: v === "none" ? "" : v })}>
                                        <SelectTrigger data-testid="edit-product-quality"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Belirtilmedi</SelectItem>
                                            {QUALITY_OPTIONS.map((qo) => <SelectItem key={qo} value={qo}>{qo}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <label className="flex cursor-pointer items-center gap-4 rounded-xl border border-dashed p-3 transition-colors hover:border-terra-500/50" data-testid="edit-product-image-drop">
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files[0], setEditImg)} data-testid="edit-product-image-input" />
                                <img src={editImg?.preview || edit.image} alt="" className="h-14 w-14 rounded-lg object-cover" />
                                <div className="text-xs text-muted-foreground">
                                    <p className="font-semibold text-foreground">{editImg ? (editImg.uploading ? "Sıkıştırılıyor..." : "Yeni görsel hazır") : "Görseli değiştir"}</p>
                                    {editImg?.stats && <p className="mt-0.5 font-medium text-emerald-700" data-testid="edit-product-compression-info">{fmtKB(editImg.stats.orig)} → {fmtKB(editImg.stats.comp)} • WebP</p>}
                                </div>
                            </label>
                            <button type="button" onClick={() => setPickerFor("edit")} className="text-xs font-medium text-primary hover:underline" data-testid="edit-product-pick-library">
                                veya Ortam Kütüphanesi'nden seç
                            </button>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setEdit(null)}>Vazgeç</Button>
                                <Button onClick={submitEdit} disabled={saving || editImg?.uploading} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="edit-product-save">Kaydet</Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={!!adjust} onOpenChange={() => setAdjust(null)}>
                <DialogContent data-testid="stock-adjust-dialog">
                    {adjust && (
                        <>
                            <DialogHeader><DialogTitle className="font-display">Stok Düzelt — {adjust.product_name}</DialogTitle></DialogHeader>
                            <p className="text-sm text-muted-foreground">Mevcut: <strong>{fmtQty4(adjust.stock, adjust.unit)} {adjust.unit}</strong></p>
                            <Input type="number" min={0} value={newStock} onChange={(e) => setNewStock(e.target.value)} data-testid="stock-adjust-input" />
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setAdjust(null)}>Vazgeç</Button>
                                <Button onClick={save} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="stock-adjust-save">Kaydet</Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={confirmZero}
                onClose={() => setConfirmZero(false)}
                onConfirm={doSave}
                title="Stok sıfırlanıyor"
                description={adjust ? `${adjust.product_name} stokunu ${adjust.stock} ${adjust.unit} → 0 ${adjust.unit} olarak değiştirmek üzeresiniz. Ürün katalogda "Stokta Yok" görünecek. Onaylıyor musunuz?` : ""}
                confirmText="Evet, Sıfırla"
                destructive
                testId="stock-zero-confirm"
            />

            <Dialog open={excelOpen} onOpenChange={setExcelOpen}>
                <DialogContent className="max-w-2xl" data-testid="excel-import-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Excel ile İçe Aktar</DialogTitle>
                        <DialogDescription>Şablonu indirip doldurun. Önce önizleme gösterilir, onayınızla uygulanır.</DialogDescription>
                    </DialogHeader>
                    <div className="mb-1 flex gap-2" data-testid="excel-module-toggle">
                        {[["stock", "Stok/Fiyat Güncelleme"], ["products", "Yeni Ürün Ekleme"]].map(([k, l]) => (
                            <button key={k} onClick={() => { setExcelModule(k); setExcelPreview(null); }} className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${excelModule === k ? "border-primary bg-primary text-primary-foreground" : "hover:border-terra-500/50"}`} data-testid={`excel-module-${k}`}>{l}</button>
                        ))}
                    </div>
                    {!excelPreview ? (
                        <div className="space-y-4">
                            <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-dashed p-8 text-center transition-colors hover:border-terra-500/50" data-testid="excel-upload-drop">
                                <input type="file" accept=".xlsx" className="hidden" disabled={excelBusy} onChange={(e) => uploadExcel(e.target.files[0])} data-testid="excel-upload-input" />
                                <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
                                <p className="text-sm font-semibold">{excelBusy ? "Okunuyor ve doğrulanıyor..." : ".xlsx dosyası seçin"}</p>
                                <p className="text-xs text-muted-foreground">{excelModule === "stock" ? "Sütunlar: SKU, Ürün Adı, Birim Fiyat, Stok, MOQ" : "Sütunlar: Ürün Adı, Kategori, Birim, Fiyat, Stok, MOQ, Barkod, Teslim (gün)"}</p>
                            </label>
                            <div className="flex items-center justify-between text-xs">
                                <button type="button" onClick={() => downloadFile(`${API}/excel/template?module=${excelModule}`)} className="font-medium text-primary hover:underline" data-testid="excel-dialog-template">Şablonu indir</button>
                                <button type="button" onClick={openHistory} className="font-medium text-primary hover:underline" data-testid="excel-history-button">Yükleme geçmişi</button>
                            </div>
                            {excelHistory && (
                                <div className="max-h-40 overflow-y-auto rounded-lg border" data-testid="excel-history-list">
                                    {excelHistory.map((h) => (
                                        <div key={h.import_id} className="flex justify-between border-b px-3 py-2 text-xs last:border-0">
                                            <span className="font-mono font-semibold">{h.import_id}</span>
                                            <span className="text-muted-foreground">{h.filename} • {h.total} satır • {h.ok} geçerli • {h.failed} hatalı</span>
                                            <Badge variant="outline" className="text-[10px]">{h.status === "committed" ? `Uygulandı (${h.applied})` : "Önizleme"}</Badge>
                                        </div>
                                    ))}
                                    {!excelHistory.length && <p className="p-4 text-center text-muted-foreground">Henüz yükleme yok.</p>}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div className="flex items-center gap-3 rounded-lg bg-muted p-3 text-sm" data-testid="excel-preview-summary">
                                <span className="font-mono text-xs font-bold">{excelPreview.import_id}</span>
                                <Badge variant="outline" className="border-emerald-200 bg-emerald-100 text-emerald-800">{excelPreview.ok} geçerli</Badge>
                                {excelPreview.failed > 0 && <Badge variant="outline" className="border-red-200 bg-red-100 text-red-700">{excelPreview.failed} hatalı</Badge>}
                            </div>
                            <div className="max-h-64 overflow-y-auto rounded-lg border" data-testid="excel-preview-rows">
                                <table className="w-full text-xs">
                                    <thead><tr className="border-b bg-muted/60 text-left"><th className="p-2">Satır</th><th className="p-2">SKU</th><th className="p-2">Ürün</th><th className="p-2">Fiyat</th><th className="p-2">Stok</th><th className="p-2">Durum</th></tr></thead>
                                    <tbody>
                                        {excelPreview.rows.map((r) => (
                                            <tr key={r.satir} className={`border-b last:border-0 ${r.durum === "hata" ? "bg-red-50" : ""}`} data-testid={`excel-row-${r.satir}`}>
                                                <td className="p-2">{r.satir}</td>
                                                <td className="p-2 font-mono">{r.sku || r.kategori || "—"}</td>
                                                <td className="p-2">{r.urun}</td>
                                                <td className="p-2">{r.price ?? "—"}</td>
                                                <td className="p-2">{r.stock ?? "—"}</td>
                                                <td className="p-2">
                                                    {r.durum === "ok" ? <span className="font-semibold text-emerald-700">Geçerli</span> : (
                                                        <span className="text-red-700">{r.hatalar.map((h) => `${h.alan}: ${h.neden}`).join(" • ")}</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setExcelPreview(null)}>Geri</Button>
                                <Button onClick={commitExcel} disabled={excelBusy || excelPreview.ok === 0} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="excel-commit-button">
                                    {excelBusy ? "Uygulanıyor..." : `Onayla ve Uygula (${excelPreview.ok} satır)`}
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <MediaPicker
                open={!!pickerFor}
                onClose={() => setPickerFor(null)}
                initialQuery={pickerFor === "add" ? addForm.name : edit?.product_name || ""}
                onSelect={(url) => {
                    const sel = { preview: url, url, stats: null, uploading: false };
                    if (pickerFor === "add") setImg(sel); else setEditImg(sel);
                    setPickerFor(null);
                }}
            />
        </div>
    );
}
